import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Card, Form, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRompslompMappings, putRompslompMapping } from '../../../services/RompslompService';
import { decodeEntities, useRompslompLabels } from '../../../utils/rompslompLabels';

const NOT_MAPPED = '';

function NotMapped() {
    const { t } = useTranslation();
    return <Badge bg="danger" className="ms-2">{t('rompslomp.mappings.notMapped')}</Badge>;
}

// A <select> over cached Rompslomp objects; the empty option clears the mapping ("not mapped").
function Pick({ value, options, onPick, label, testId }) {
    const { t } = useTranslation();
    return (
        <Form.Select size="sm" aria-label={label} data-testid={testId} value={value ?? NOT_MAPPED}
            onChange={(e) => onPick(e.target.value === NOT_MAPPED ? null : Number(e.target.value))}>
            <option value={NOT_MAPPED}>{t('rompslomp.mappings.notMapped')}</option>
            {value != null && !options.some((o) => o.id === value) && <option value={value}>#{value}</option>}
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </Form.Select>
    );
}

const accountOptions = (list, labels) => list.map((a) => ({ id: a.id, label: `${a.path || decodeEntities(a.name)} - ${labels.account(a)}` }));

export default function RompslompMappings() {
    const { t } = useTranslation();
    const labels = useRompslompLabels();
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        try { setData((await getRompslompMappings()).data); } catch (err) { toast.error(err.response?.data?.error || t('rompslomp.errors.generic')); }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const save = async (area, key, field, value) => {
        try { setData((await putRompslompMapping({ area, key, [field]: value })).data); } catch (err) { toast.error(err.response?.data?.error || t('rompslomp.errors.generic')); }
    };

    if (!data) return <Spinner animation="border" />;
    const { options } = data;
    const empty = options.vat_types.length === 0;
    const revenue = accountOptions(options.revenue_accounts, labels);
    const payAcc = accountOptions(options.payment_accounts, labels);
    const costs = accountOptions(options.cost_accounts, labels);
    const vatOpts = options.vat_types.map((v) => ({ id: v.id, label: labels.vatType(v) }));
    const contacts = options.suppliers.map((c) => ({ id: c.id, label: decodeEntities(c.name) }));
    const row = (area, key) => data[area][key] || {};

    return (
        <>
            {empty && <Alert variant="warning">{t('rompslomp.mappings.syncFirst')}</Alert>}
            <p className="text-muted">{t('rompslomp.mappings.hint')}</p>

            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.mappings.sales')}</h5></Card.Header>
                <Card.Body>
                    <Table className="rompslomp-stack" size="sm" responsive data-testid="sales-mapping">
                        <thead><tr><th>{t('rompslomp.mappings.rate')}</th><th>{t('rompslomp.mappings.ledger')}</th><th>{t('rompslomp.mappings.vatCode')}</th><th>{t('rompslomp.mappings.vatLedger')}</th></tr></thead>
                        <tbody>
                            {data.keys.rates.map((r) => (
                                <tr key={r}>
                                    <td data-label={t('rompslomp.mappings.rate')}>{r}%</td>
                                    <td data-label={t('rompslomp.mappings.ledger')}>
                                        <Pick label={`${t('rompslomp.mappings.ledger')} ${r}%`} value={row('sales', `rate:${r}`).account_id} options={revenue} onPick={(v) => save('sales', `rate:${r}`, 'account_id', v)} />
                                        {!row('sales', `rate:${r}`).account_id && <NotMapped />}
                                    </td>
                                    <td data-label={t('rompslomp.mappings.vatCode')}>
                                        <Pick label={`${t('rompslomp.mappings.vatCode')} ${r}%`} value={row('vat', `rate:${r}`).vat_type_id} options={vatOpts} onPick={(v) => save('vat', `rate:${r}`, 'vat_type_id', v)} />
                                        {!row('vat', `rate:${r}`).vat_type_id && <NotMapped />}
                                        {data.suggested_vat[r] && !row('vat', `rate:${r}`).vat_type_id && (
                                            <Form.Text className="d-block text-muted">{t('rompslomp.mappings.suggested')}: {vatOpts.find((o) => o.id === data.suggested_vat[r])?.label}</Form.Text>
                                        )}
                                    </td>
                                    <td data-label={t('rompslomp.mappings.vatLedger')}>
                                        {r === '0' ? '-' : (
                                            <Pick label={`${t('rompslomp.mappings.vatLedger')} ${r}%`} value={row('sales', `vat:${r}`).account_id} options={accountOptions(options.ledger_accounts, labels)} onPick={(v) => save('sales', `vat:${r}`, 'account_id', v)} />
                                        )}
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td data-label={t('rompslomp.mappings.rate')}>{t('rompslomp.mappings.shipping')}</td>
                                <td data-label={t('rompslomp.mappings.ledger')}>
                                    <Pick label={t('rompslomp.mappings.shipping')} value={row('sales', 'shipping').account_id} options={revenue} onPick={(v) => save('sales', 'shipping', 'account_id', v)} />
                                    {!row('sales', 'shipping').account_id && <NotMapped />}
                                </td>
                                <td colSpan="2" className="text-muted" data-label={t('rompslomp.mappings.vatCode')}>{t('rompslomp.mappings.shippingHint')}</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Form.Text className="text-muted">{t('rompslomp.mappings.vatLedgerHint')}</Form.Text>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.mappings.payments')}</h5></Card.Header>
                <Card.Body>
                    <p className="text-muted small">{t('rompslomp.mappings.paymentsHint')}</p>
                    <Table className="rompslomp-stack" size="sm" responsive data-testid="payment-mapping">
                        <thead><tr><th>{t('rompslomp.mappings.gateway')}</th><th>{t('rompslomp.mappings.rompslompMethod')}</th><th>{t('rompslomp.mappings.paymentLedger')}</th></tr></thead>
                        <tbody>
                            {data.keys.payment_gateways.map((m) => (
                                <tr key={m}>
                                    <td data-label={t('rompslomp.mappings.gateway')}>
                                        <strong>{t(`rompslomp.mappings.gateways.${m}`)}</strong>
                                        {(data.seen_gateways?.[m] || []).map((g) => <div key={g.label} className="small text-muted">{t('rompslomp.mappings.seen')}: {g.label} ({g.count})</div>)}
                                    </td>
                                    <td data-label={t('rompslomp.mappings.rompslompMethod')}>
                                        <Form.Select size="sm" aria-label={`${t('rompslomp.mappings.rompslompMethod')} ${t(`rompslomp.mappings.gateways.${m}`)}`} value={row('payment', m).payment_method || ''}
                                            onChange={(e) => save('payment', m, 'payment_method', e.target.value || null)}>
                                            <option value="">{t('rompslomp.mappings.notMapped')}</option>
                                            {options.payment_codes.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </Form.Select>
                                        {!row('payment', m).payment_method && <NotMapped />}
                                    </td>
                                    <td data-label={t('rompslomp.mappings.paymentLedger')}>
                                        <Pick label={`${t('rompslomp.mappings.paymentLedger')} ${t(`rompslomp.mappings.gateways.${m}`)}`} value={row('payment', m).account_id} options={payAcc} onPick={(v) => save('payment', m, 'account_id', v)} />
                                        {!row('payment', m).account_id && <NotMapped />}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header><h5 className="mb-0">{t('rompslomp.mappings.treatments')}</h5></Card.Header>
                <Card.Body>
                    <p className="text-muted">{t('rompslomp.mappings.treatmentsHint')}</p>
                    <Table className="rompslomp-stack" size="sm" responsive data-testid="treatment-mapping">
                        <thead><tr><th>{t('rompslomp.expenses.treatment')}</th><th>{t('rompslomp.mappings.vatCode')}</th></tr></thead>
                        <tbody>
                            {data.keys.non_dutch_treatments.map((k) => (
                                <tr key={k}>
                                    <td data-label={t('rompslomp.expenses.treatment')}>{t(`rompslomp.treatments.${k}`)}</td>
                                    <td data-label={t('rompslomp.mappings.vatCode')}>
                                        <Pick label={`${t('rompslomp.treatments.' + k)} ${t('rompslomp.mappings.vatCode')}`} value={(data.treatment[k] || {}).vat_type_id} options={vatOpts} onPick={(v) => save('treatment', k, 'vat_type_id', v)} />
                                        {!(data.treatment[k] || {}).vat_type_id && <NotMapped />}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header><h5 className="mb-0">{t('rompslomp.mappings.expenses')}</h5></Card.Header>
                <Card.Body>
                    <p className="text-muted">{t('rompslomp.mappings.expensesHint')}</p>
                    <Table className="rompslomp-stack" size="sm" responsive data-testid="supplier-mapping">
                        <thead><tr><th>{t('rompslomp.mappings.supplier')}</th><th>{t('rompslomp.mappings.expenseCount')}</th><th>{t('rompslomp.mappings.contact')}</th><th>{t('rompslomp.mappings.costLedger')}</th><th>{t('rompslomp.expenses.treatment')}</th><th>{t('rompslomp.expenses.country')}</th></tr></thead>
                        <tbody>
                            {data.suppliers.map((s) => (
                                <tr key={s.key}>
                                    <td data-label={t('rompslomp.mappings.supplier')}>
                                        <strong>{s.supplier_name || s.names[0]}</strong>
                                        {(s.supplier_name ? s.names.filter((n) => n !== s.supplier_name) : s.names.slice(1)).length > 0
                                            && <div className="small text-muted">{t('rompslomp.mappings.alsoWrittenAs', { names: (s.supplier_name ? s.names.filter((n) => n !== s.supplier_name) : s.names.slice(1)).join(' / ') })}</div>}
                                    </td>
                                    <td data-label={t('rompslomp.mappings.expenseCount')}>{s.expenses}</td>
                                    <td data-label={t('rompslomp.mappings.contact')}>
                                        <Pick label={`${t('rompslomp.mappings.contact')} ${s.key}`} value={s.mapping?.contact_id} options={contacts} onPick={(v) => save('supplier', s.key, 'contact_id', v)} />
                                        {!(s.mapping?.contact_id && s.mapping?.confirmed) && <NotMapped />}
                                        {!s.mapping?.contact_id && s.suggestions.map((g) => (
                                            <Form.Text as="button" type="button" key={g.id} className="d-block btn btn-link btn-sm p-0 text-start" onClick={() => save('supplier', s.key, 'contact_id', g.id)}>
                                                {t('rompslomp.mappings.suggested')}: {decodeEntities(g.name)} ({Math.round(g.score * 100)}%) - {t('rompslomp.mappings.confirm')}
                                            </Form.Text>
                                        ))}
                                    </td>
                                    <td data-label={t('rompslomp.mappings.costLedger')}>
                                        <Pick label={`${t('rompslomp.mappings.costLedger')} ${s.key}`} value={s.mapping?.account_id} options={costs} onPick={(v) => save('supplier', s.key, 'account_id', v)} />
                                        {!s.mapping?.account_id && <NotMapped />}
                                    </td>
                                    <td data-label={t('rompslomp.expenses.treatment')}>
                                        <Form.Select size="sm" aria-label={`${t('rompslomp.expenses.treatment')} ${s.key}`} value={s.mapping?.vat_treatment || ''}
                                            onChange={(e) => save('supplier', s.key, 'vat_treatment', e.target.value || null)}>
                                            <option value="">{t('rompslomp.mappings.notMapped')}</option>
                                            {data.keys.treatments.map((k) => <option key={k} value={k}>{t(`rompslomp.treatments.${k}`)}</option>)}
                                        </Form.Select>
                                        {!s.mapping?.vat_treatment && <NotMapped />}
                                    </td>
                                    <td className="rompslomp-narrow" data-label={t('rompslomp.expenses.country')}>
                                        <Form.Control size="sm" maxLength={2} key={`${s.key}-${s.mapping?.country || ''}`} aria-label={`${t('rompslomp.expenses.country')} ${s.key}`} defaultValue={s.mapping?.country || ''}
                                            onBlur={(e) => { const v = e.target.value.trim().toUpperCase(); if (v !== (s.mapping?.country || '')) save('supplier', s.key, 'country', v || null); }} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Form.Text className="text-muted">{t('rompslomp.mappings.expenseVatHint')}</Form.Text>
                </Card.Body>
            </Card>
        </>
    );
}
